/**
 * KT-187 — one search stack.
 *
 * T4S predictive search (`assets/predictive-search.min.js`) owns EVERY search
 * surface: the mobile drawer (`sections/search-hidden.liquid`), the desktop
 * header form (`snippets/t4s_group_btns.liquid`) — both of which carry
 * `data-predictive-search` — and the search PAGE (`sections/main-search.liquid`),
 * which resolves SKU parity server-side rather than through this controller.
 *
 * KT-214: this comment previously named `snippets/search_form.liquid` as the third
 * surface and said "all three carry data-predictive-search". That file was rendered
 * by nothing, so the claim was untestable and the file has been deleted. The comment
 * is otherwise the best description of the architecture in the repo, which is exactly
 * why the one inaccurate clause was worth fixing rather than leaving.
 *
 * This file used to be a second, independent autocomplete stack: it bound its
 * own `input` handler, fetched its own results, rendered its own dropdown, and
 * then tried to stand down at runtime by reading DOM state mid-keystroke. That
 * arbitration is deleted along with the stack it was arbitrating for.
 *
 * What is left is a set of COMPANION modules. They attach to a predictive
 * container and add panels alongside it. None of them binds a result-fetching
 * input handler, so there is nothing left to arbitrate:
 *
 *   1. Recent + popular searches  — ported from the retired stack (KT-187)
 *   2. Collections segment        — KT-112
 *   3. Enter / on-screen "Go"     — submits the native GET form
 *
 * SKU parity (KT-110, `PCDMB12B` / `PCDMB12B+HGS02`) is NOT implemented here
 * and never was: it lives server-side in `sections/main-search.liquid`, which
 * the drawer re-renders through. Retiring this stack does not touch it.
 */
(function () {
  'use strict';

  var RECENT_KEY = 'kt-recent-searches';
  var RECENT_MAX = 10;
  var RECENT_SHOWN = 5;

  /* ---------------------------------------------------------------- store -- */

  function loadRecent() {
    try {
      var raw = localStorage.getItem(RECENT_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter(function (t) { return typeof t === 'string'; }) : [];
    } catch (e) {
      return [];
    }
  }

  function saveRecent(query) {
    var term = (query || '').trim();
    if (term.length < 2) return;
    var list = loadRecent().filter(function (t) { return t !== term; });
    list.unshift(term);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) { /* quota / private mode — recent searches are optional */ }
  }

  function clearRecent() {
    try { localStorage.removeItem(RECENT_KEY); } catch (e) { /* see saveRecent */ }
  }

  /* ------------------------------------------------------------ analytics -- */

  function trackSearch(query, source) {
    if (window.ktSearchAnalytics && typeof window.ktSearchAnalytics.logEvent === 'function') {
      window.ktSearchAnalytics.logEvent('search_submitted', {
        search_term: query,
        source: source || 'manual',
        timestamp: Date.now()
      });
    }
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'search', search_term: query, search_source: source || 'manual' });
    }
  }

  /* ---------------------------------------------------------------- utils -- */

  function searchUrl(term) {
    return '/search?type=product&q=' + encodeURIComponent(term);
  }

  function termsFromHotkeys(container) {
    // The theme already renders `settings.list_hotkey` as a "Quick search"
    // strip inside the drawer — that IS the popular-terms list. Reuse it so a
    // single theme setting drives popular terms on every surface.
    //
    // KT-193: that strip is markup of the DRAWER (sections/search-hidden.liquid
    // renders `[data-listKey]` inside its own form), but this panel also mounts on
    // the HEADER stack, which has no strip of its own. Scoping the lookup to
    // `container` therefore returned [] on the header, so `popular` was empty,
    // `section()` returned null, and `panel.hidden` stayed true forever — the
    // pre-query panel NEVER appeared on the surface almost everyone actually uses.
    // What users got on first click instead was the drawer's `search_prs_suggest`
    // collection (bg-evolve), i.e. BG light switches on a Geberit page, which reads
    // as "search is broken". Measured locally 2026-08-16: header container 0 terms,
    // document 6.
    //
    // Fall back to the document so a single theme setting really does drive every
    // surface, which is what the comment above always claimed. `data-key` carries
    // the clean term — the visible label is joined with ", " for display and would
    // otherwise search for "BG Evolve," with the comma attached.
    var keys = container.querySelectorAll('[data-listKey] [data-key]');
    if (!keys.length) keys = document.querySelectorAll('[data-listKey] [data-key]');
    return Array.prototype.map.call(keys, function (el) {
      return (el.getAttribute('data-key') || '').trim();
    }).filter(Boolean);
  }

  /* -------------------------------------------- recent + popular searches -- */

  function initRecentPanel(container) {
    var input = container.querySelector('[data-input-search]');
    var results = container.querySelector('[data-results-search]');
    if (!input || !results || container.dataset.ktRecentPanel) return;
    container.dataset.ktRecentPanel = '1';

    // Sibling of the results node, not a child: the predictive controller
    // replaces `[data-results-search]`.innerHTML on every render and would
    // otherwise wipe this panel.
    var panel = document.createElement('div');
    panel.className = 'kt-search-panel';
    panel.hidden = true;
    results.parentNode.insertBefore(panel, results);

    var popular = termsFromHotkeys(container);

    function row(term, kind) {
      var a = document.createElement('a');
      a.className = 'kt-search-panel__item';
      a.href = searchUrl(term);
      a.dataset.ktTerm = term;
      a.dataset.ktKind = kind;
      a.textContent = term;
      return a;
    }

    function section(titleText, terms, kind, withClear) {
      if (!terms.length) return null;
      var wrap = document.createElement('div');
      wrap.className = 'kt-search-panel__section';

      var head = document.createElement('div');
      head.className = 'kt-search-panel__head';
      var title = document.createElement('span');
      title.className = 'kt-search-panel__title';
      title.textContent = titleText;
      head.appendChild(title);

      if (withClear) {
        var clear = document.createElement('button');
        clear.type = 'button';
        clear.className = 'kt-search-panel__clear';
        clear.textContent = 'Clear';
        clear.addEventListener('click', function () {
          clearRecent();
          render();
        });
        head.appendChild(clear);
      }

      var list = document.createElement('div');
      list.className = 'kt-search-panel__list';
      terms.forEach(function (term) { list.appendChild(row(term, kind)); });

      wrap.appendChild(head);
      wrap.appendChild(list);
      return wrap;
    }

    function render() {
      panel.textContent = '';
      var recentSection = section('Recent searches', loadRecent().slice(0, RECENT_SHOWN), 'recent', true);
      var popularSection = section('Popular searches', popular.slice(0, RECENT_SHOWN), 'popular', false);
      if (recentSection) panel.appendChild(recentSection);
      if (popularSection) panel.appendChild(popularSection);
      panel.hidden = !panel.firstChild;
    }

    function sync() {
      // The panel is a pre-query surface only. As soon as there is a query the
      // predictive controller owns the pane, so the panel gets out of the way.
      if (input.value.trim().length) {
        panel.hidden = true;
      } else {
        render();
      }
    }

    input.addEventListener('focus', sync);
    input.addEventListener('input', sync);

    panel.addEventListener('click', function (event) {
      var link = event.target.closest('[data-kt-term]');
      if (!link) return;
      trackSearch(link.dataset.ktTerm, link.dataset.ktKind);
      saveRecent(link.dataset.ktTerm);
    });

    var form = container.querySelector('[data-frm-search]');
    if (form) {
      form.addEventListener('submit', function () {
        var term = input.value.trim();
        if (!term) return;
        saveRecent(term);
        trackSearch(term, 'manual');
      });
    }

    sync();
  }

  /* ------------------------------------- KT-112 collections segment -------- */

  /**
   * Shopify storefront search cannot return collections, so "BG Evolve" never
   * surfaced the BG Evolve collection. Query the Predictive Search API for up
   * to 3 matching collections and render them ABOVE the product results
   * (Collections -> Products -> Guides), in a sibling the section re-render
   * never touches.
   */
  function initCollectionsSegment(container) {
    var input = container.querySelector('[data-input-search]');
    var results = container.querySelector('[data-results-search]');
    if (!input || !results || container.dataset.ktCollectionsSeg) return;
    container.dataset.ktCollectionsSeg = '1';

    var seg = document.createElement('div');
    seg.className = 'kt-search-collections';
    seg.hidden = true;
    seg.innerHTML = '<div class="kt-search-collections__title"></div><div class="kt-search-collections__list"></div>';
    results.parentNode.insertBefore(seg, results);

    var titleEl = seg.firstElementChild;
    var listEl = seg.lastElementChild;
    titleEl.textContent = (window.T4Sstrings && window.T4Sstrings.collections_label) || 'Collections';

    var timer = null;
    var lastQuery = '';

    function clear() {
      seg.hidden = true;
      listEl.textContent = '';
      lastQuery = '';
    }

    function fetchCollections(q) {
      var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=collection&resources[limit]=3';
      fetch(url)
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data || input.value.trim() !== q) return;
          var cols = (((data.resources || {}).results || {}).collections) || [];
          if (!cols.length) { clear(); return; }
          listEl.textContent = '';
          cols.forEach(function (c) {
            var a = document.createElement('a');
            a.href = c.url;
            a.textContent = c.title;
            listEl.appendChild(a);
          });
          seg.hidden = false;
        })
        .catch(function () { /* segment is progressive enhancement — fail silent */ });
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(timer);
      if (q.length < 2) { clear(); return; }
      // Matches the predictive debounce (KT-187: 180ms) so the collections
      // strip and the product list land together rather than in two paints.
      timer = setTimeout(function () {
        if (q === lastQuery) return;
        lastQuery = q;
        fetchCollections(q);
      }, 180);
    });
  }

  /* ------------------------------------------- KT-191 drawer loading states -- */

  /**
   * Between keystroke and results the pane was blank, which is indistinguishable
   * from broken — the exact perception that was reported.
   *
   * The predictive controller does show the skeleton, but only once its debounce
   * has elapsed and the request is actually starting. Show it on the keystroke
   * itself so there is never a blank frame, and give the failure path a visible
   * retry line instead of silently closing the drawer.
   */
  function initDrawerStates(container) {
    var input = container.querySelector('[data-input-search]');
    var results = container.querySelector('[data-results-search]');
    var skeleton = container.querySelector('[data-skeleton-search]');
    if (!input || !results || !skeleton || container.dataset.ktDrawerStates) return;
    container.dataset.ktDrawerStates = '1';

    var error = document.createElement('div');
    error.className = 'kt-search-error';
    error.hidden = true;
    results.parentNode.insertBefore(error, results);

    function showSkeleton() {
      error.hidden = true;
      skeleton.classList.remove('t4s-dn');
      skeleton.style.display = '';
    }

    input.addEventListener('input', function () {
      if (input.value.trim().length) showSkeleton();
    });

    window.ktSearchDrawerError = function (errored, query) {
      if (errored !== container) return;
      skeleton.style.display = 'none';
      error.textContent = '';

      var message = document.createElement('span');
      message.className = 'kt-search-error__text';
      message.textContent = "Couldn't load suggestions.";

      var retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'kt-search-error__retry';
      retry.textContent = 'Try again';
      retry.addEventListener('click', function () {
        error.hidden = true;
        showSkeleton();
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });

      var full = document.createElement('a');
      full.className = 'kt-search-error__link';
      full.href = searchUrl(query || input.value.trim());
      full.textContent = 'Search anyway';

      error.appendChild(message);
      error.appendChild(retry);
      error.appendChild(full);
      error.hidden = false;
    };
  }

  /* ------------------------------------------- KT-189 drawer focus + scroll -- */

  /**
   * The bottom-toolbar Search button is the single entry point to the drawer.
   * The theme opens it and `layout/theme.liquid` focuses the input, but nothing
   * kept focus inside while open, returned it to the trigger on close, or put
   * the page back where it was. Driven off the drawer's own `aria-hidden`, so
   * it works no matter which control opened it.
   */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function initDrawerFocus(drawer) {
    if (drawer.dataset.ktFocusManaged) return;
    drawer.dataset.ktFocusManaged = '1';

    var trigger = null;
    var scrollY = 0;
    var isOpen = false;

    document.addEventListener('pointerdown', function (event) {
      var candidate = event.target.closest &&
        event.target.closest('[data-search-toggle], [data-drawer-options*="#t4s-search-hidden"]');
      if (candidate) trigger = candidate;
    }, true);

    function visibleFocusable() {
      return Array.prototype.filter.call(drawer.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      });
    }

    drawer.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab') return;
      var items = visibleFocusable();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    new MutationObserver(function () {
      var open = drawer.getAttribute('aria-hidden') === 'false';
      if (open === isOpen) return;
      isOpen = open;

      if (open) {
        scrollY = window.scrollY || window.pageYOffset || 0;
        return;
      }

      // Closing: put the page and the keyboard focus back where they were, so a
      // shopper who opened search from halfway down a collection returns there.
      window.scrollTo({ top: scrollY, behavior: 'auto' });
      if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
    }).observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  /* ----------------------------------------------------------------- boot -- */

  function attach() {
    var containers = document.querySelectorAll('[data-predictive-search]');
    if (!containers.length) return false;
    Array.prototype.forEach.call(containers, function (container) {
      initRecentPanel(container);
      initCollectionsSegment(container);
      initDrawerStates(container);
    });
    var drawer = document.getElementById('t4s-search-hidden');
    if (drawer) initDrawerFocus(drawer);
    return true;
  }

  function boot() {
    attach();
    // The drawer section is fetched and appended lazily by the predictive
    // bootstrap (?section_id=search-hidden) — watch for it, then stop.
    var observer = new MutationObserver(function () {
      if (document.querySelector('[data-predictive-search][data-kt-recent-panel]') &&
          !document.querySelector('[data-predictive-search]:not([data-kt-recent-panel])')) {
        observer.disconnect();
      }
      attach();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * The predictive `keyup` handler consumes Enter even when no suggestion is
   * selected. Submit the native GET form from keydown so typed searches — and
   * the on-screen keyboard "Go" key — stay dependable.
   */
  function bootPredictiveEnterFallback() {
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' || event.isComposing) return;
      var input = event.target.closest('[data-predictive-search] [data-input-search]');
      if (!input || !input.value.trim()) return;
      var container = input.closest('[data-predictive-search]');
      if (container.querySelector('[aria-selected="true"] a, [aria-selected="true"] button')) return;
      var form = input.closest('form');
      if (!form) return;
      saveRecent(input.value.trim());
      trackSearch(input.value.trim(), 'keyboard');
      event.preventDefault();
      event.stopImmediatePropagation();
      form.requestSubmit ? form.requestSubmit() : form.submit();
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
      bootPredictiveEnterFallback();
    });
  } else {
    boot();
    bootPredictiveEnterFallback();
  }
})();
